import { AppProps } from './app'
import { EmailPayload } from './machines/emailReport.machine'
import { PaymentFrequency, SelectOptions } from './types/global'

const getDefaultOption = <T>(options: SelectOptions<T>): T => {
  return options.find((option) => Boolean(option?.default))?.value as T
}

const defaultOptions: Pick<AppProps, 'annualConsumptionOptions' | 'paymentFrequencyOptions'> = {
  annualConsumptionOptions: [
    { label: '0 - 5,000', value: 3_034, data: { averageCost: 1_153 } },
    { label: '5,001 - 10,000', value: 6_906, data: { averageCost: 2_196 } },
    { label: '10,001 - 20,000', value: 12_988, data: { averageCost: 3_836 } },
    { label: '20,001 - 50,000', value: 29_445, data: { averageCost: 8_119 }, default: true },
    { label: '50,001 - 100,000', value: 68_956, data: { averageCost: 17_951 } },
    { label: '100,001 - 200,000', value: 137_747, data: { averageCost: 32_639 } },
    { label: '200,001 - 350,000', value: 258_840, data: { averageCost: 56_252 } },
    { label: '350,001 - 500,000', value: 415_147, data: { averageCost: 86_347 } },
    { label: '500,001 - 750,000', value: 616_677, data: { averageCost: 130_268 } },
    { label: '750,001 - 1,000,000', value: 867_998, data: { averageCost: 175_104 } },
  ],
  paymentFrequencyOptions: [
    { label: 'Annually', value: PaymentFrequency.Annually, default: true },
    { label: 'Quarterly', value: PaymentFrequency.Quarterly },
    { label: 'Bimonthly', value: PaymentFrequency.Bimonthly },
    { label: 'Monthly', value: PaymentFrequency.Monthly },
    { label: 'Fortnightly', value: PaymentFrequency.Fortnightly },
    { label: 'Weekly', value: PaymentFrequency.Weekly },
  ],
}

const defaultValues: Pick<
  AppProps,
  | 'costPerUnit'
  | 'emissionFactor'
  | 'carEmissionFactor'
  | 'gst'
  | 'initialAnnualConsumption'
  | 'initialNaturalPowerContribution'
  | 'initialCurrentCost'
  | 'initialPaymentFrequency'
  | 'emailTimeout'
> = {
  costPerUnit: 0.04968,
  emissionFactor: 0.68,
  carEmissionFactor: 2.715,
  gst: 1.1,
  initialNaturalPowerContribution: 0,
  initialCurrentCost: 0,
  initialAnnualConsumption: getDefaultOption(defaultOptions.annualConsumptionOptions),
  initialPaymentFrequency: getDefaultOption(defaultOptions.paymentFrequencyOptions),
  emailTimeout: 10_000,
}

const defaultCallbacks: Pick<AppProps, 'contactCallback' | 'emailCallback'> = {
  contactCallback: () => {
    const contactForm = document.querySelector('#buyOnline')
    if (contactForm) {
      contactForm.scrollIntoView({ behavior: 'smooth' })
    } else {
      console.error('Could not find contact form.')
    }
  },
  emailCallback: (payload: EmailPayload) => {
    return new Promise((resolve, reject) => {
      fetch('/SendEmailCalculator/', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((res) => res.json())
        .then((data) => resolve(data))
        .catch((err) => reject(err))
    })
  },
}

const defaultLabels: Pick<
  AppProps,
  'paymentFrequencyLabel' | 'currentCostLabel' | 'estimatedCostLabel' | 'estimatedNaturalPowerCostLabel'
> = {
  paymentFrequencyLabel: 'Payment frequency',
  currentCostLabel: 'Current cost',
  estimatedCostLabel: 'Estimated cost',
  estimatedNaturalPowerCostLabel: 'Estimated total cost with NaturalPower',
}

const defaultMessages: Pick<
  AppProps,
  | 'emailInputPlaceholder'
  | 'disabledMessage'
  | 'requiredMessage'
  | 'invalidMessage'
  | 'successMessage'
  | 'errorMessage'
  | 'timeoutMessage'
> = {
  emailInputPlaceholder: 'Email me the results',
  disabledMessage: 'NaturalPower contribution is required.',
  requiredMessage: 'Enter a valid email address to receive the results.',
  invalidMessage: 'A valid email address is required.',
  successMessage: 'Thank you! Your results have been sent.',
  errorMessage: 'An error occured whilst sending the email.',
  timeoutMessage: 'The attempt to email your results took too long.',
}

const defaultInformation: Pick<AppProps, 'tooltips' | 'footnotes' | 'benefits'> = {
  tooltips: {
    'Estimated annual consumption':
      'This is the average amount of energy for a small-medium sized business. Simply choose from the drop down to select an alternative estimated range. For a more accurate view, login to your MyAccount, check your latest bill or speak to your Account Manager.',
    'Payment frequency': 'This is how often your business is billed.',
    'Current bill':
      'Your average bill amount based on the above consumption. For a more accurate view, login to your MyAccount, check your latest bill or speak to your Account Manager',
    'Estimated cost':
      'Your NaturalPower contribution cost based on the % you have chosen to offset your traditional energy use. Use the slider to change your contribution if needed.',
    'Estimated total cost with NaturalPower':
      'This is your total bill cost, including your NaturalPower contribution based on the % you have chosen to offset your traditional energy use (25%, 50%, 75% and 100%).',
  },
  footnotes: {
    '*': 'Traditional generation sources: Traditional generation consists of both renewable and non-renewable sources such as coal, wind, solar, hydro and gas.',
    '1': 'Tonnes of CO<sub>2</sub> saved: This is the amount of CO<sub>2</sub> you could offset based on your NaturalPower contribution (25%, 50%, 75%, 100%). An estimate only based on National Greenhouse Account Factors as published by the Australian Government, Aug 2021 <a href="https://www.industry.gov.au/sites/default/files/August%202021/document/national-greenhouse-accounts-factors-2021.pdf" target="_blank" rel="nofollow noopener">National Greenhouse Accounts Factors - August 2021 (industry.gov.au)</a>',
    '2': 'Cars removed from the road: The average CO<sub>2</sub>  equivalent emissions for a medium sized sedan sold in Australia is 180 grams/km, based on 15,000km p.a. resulting in 2.7 tonnes of CO<sub>2</sub> -e p.a. (Source: Australian Government Green Vehicle Guide – Understanding Emissions: <a href="https://www.greenvehicleguide.gov.au" target="_blank" rel="nofollow noopener">Vehicle emissions | Green Vehicle Guide</a>)',
    '3': 'Estimated cost: NaturalPower incurs a premium of 5.4648 (incl. GST) cents per kWh. Pricing as at 1 July 2022. Pricing subject to change.',
  },
  benefits: [
    {
      image: 'https://synergy-naturalpower-calculator.vercel.app/images/GreenPower-Accredited.png',
      title: 'GreenPower Accredited',
      description:
        'Through your purchase of NaturalPower, your business can now use GreenPower&rsquo;s logo to showcase your environmental commitment to your customers.',
    },
    {
      title: 'NABERS, RE100, BCORP and SBTi Recognition',
      description:
        'GreenPower accreditation will give your business recognition among national and global climate change bodies - heightening exposure of your commitment to reducing Scope 2 emissions.',
    },
    {
      title: 'National Greenhouse and Energy Reporting (NGERS)',
      description:
        'Disclose your GreenPower purchase as part of your NGERS reporting, to showcase your business is investing in the renewable energy sector.',
    },
    {
      title: 'Environmental Certifications',
      description:
        'Your NaturalPower investment will allow you to potentially access environmental certifications such as Windmade and GreenBizChec.',
    },
    {
      title: 'GreenPower website / social media promotion',
      description:
        'Your business may be featured on the GreenPower website, Facebook and LinkedIn page, recognising the investment your business has taken to reduce your CO<sub>2</sub> emissions.',
    },
  ],
}

// const overrides = (): Partial<AppProps> => {
//   // TODO: Partial or complete context retrieved from cookie/localstorage
//   try {
//     const stored = localStorage.getItem('NaturalPowerCalculator')
//     if (stored) {
//       const { naturalPowerRatio, currentCost, paymentFrequency, annualConsumption } = JSON.parse(stored)
//       return Object.fromEntries(
//         Object.entries({
//           initialNaturalPowerContribution: naturalPowerRatio,
//           initialCurrentCost: currentCost,
//           initialAnnualConsumption: annualConsumption,
//           initialPaymentFrequency: paymentFrequency,
//         }).filter(([, value]) => value !== undefined),
//       )
//     }
//   } catch (error) {
//     return {}
//   }

//   return {}
// }

const initialValues: AppProps = {
  ...defaultValues,
  ...defaultOptions,
  ...defaultCallbacks,
  ...defaultLabels,
  ...defaultMessages,
  ...defaultInformation,

  // Override defaults with values from cookie/localstorage
  // ...overrides(),
}
export { initialValues }
